import Joi from 'joi';
import i18next from 'i18next';
import _intersectionWith from 'lodash/intersectionWith';
import _isEqual from 'lodash/isEqual';
import _sortBy from 'lodash/sortBy';

import { PROJECT_VIEW_STATUS, PROJECT_STATUS, PROJECT_SOURCE } from 'constants/opportunities';
import { createFormValidator } from 'helpers/validation';
import { locationsToRegionData, regionDataToLocations } from 'helpers/locations';
import { keywordsToArray, isValidKeywords } from 'helpers/keywords';
import { splitOrganizations } from 'helpers/opportunities';

const validateKeywords = (value, helper) => {
  if (!isValidKeywords(value)) {
    return helper.message(i18next.t('settings.keywords.balanced_quotes'));
  }
  return true;
};

const schema = Joi.object({
  name: Joi.string()
    .max(255)
    .required()
    .messages({
      'any.required': i18next.t('form_errors.required', {
        field: i18next.t('settings.saved_search.edit_or_create.name_title'),
      }),
      'string.empty': i18next.t('form_errors.required', {
        field: i18next.t('settings.saved_search.edit_or_create.name_title'),
      }),
      'string.max': i18next.t('form_errors.max_length', {
        field: i18next.t('settings.saved_search.edit_or_create.name_title'),
      }),
    }),
  keywords: Joi.string().custom(validateKeywords).optional().allow(''),
  excludedKeywords: Joi.string().custom(validateKeywords).optional().allow(''),
}).unknown(); // Only enforce validation on defined keys.

export const validateFormValues = createFormValidator(schema);

export const preprocessFormValues = ({
  name,
  taxonomyCodes,
  locations,
  opportunityTypes,
  viewedProjects,
  status,
  opportunitySource,
  organizations,
  keywords,
  excludedKeywords,
}) => {
  const { organizationNames, organizationUuids } = splitOrganizations(organizations);
  return {
    name,
    taxonomyCodeIds: taxonomyCodes.map(({ taxonomyCodeID }) => taxonomyCodeID),
    regionData: locationsToRegionData(locations),
    projectTypes: opportunityTypes,
    viewedProjects: viewedProjects === PROJECT_VIEW_STATUS.allStatuses ? null : viewedProjects,
    status: status === PROJECT_STATUS.allOpportunities ? null : status,
    source: opportunitySource === PROJECT_SOURCE.allSources ? null : opportunitySource,
    organizationNames,
    organizationUuids,
    keywords: keywordsToArray(keywords),
    excludedKeywords: keywordsToArray(excludedKeywords),
  };
};

export const processExistingProfile = profile => {
  if (!profile) {
    return;
  }
  const {
    regionData,
    projectTypes,
    viewedProjects,
    status,
    source,
    keywords,
    excludedKeywords,
    ...rest
  } = profile;
  return {
    locations: regionDataToLocations(regionData),
    opportunityTypes: projectTypes,
    viewedProjects: viewedProjects || PROJECT_VIEW_STATUS.allStatuses,
    status: status || PROJECT_STATUS.allOpportunities,
    opportunitySource: source || PROJECT_SOURCE.allSources,
    keywords: keywords.join(', '),
    excludedKeywords: excludedKeywords.join(', '),
    ...rest,
  };
};

export const processOpportunitiesFilters = filters => {
  if (!filters) {
    return;
  }

  const {
    locations,
    organizations,
    sources,
    statuses,
    viewStatus,
    types,
    taxonomyCodes,
    keywords,
    excludedKeywords,
  } = filters;

  return {
    locations,
    organizations,
    opportunityTypes: types,
    viewedProjects: viewStatus,
    status: statuses,
    opportunitySource: sources,
    taxonomyCodes: taxonomyCodes ?? [],
    keywords: (keywords ?? []).join(', '),
    excludedKeywords: (excludedKeywords ?? []).join(', '),
  };
};

/**
 * Compares two arrays of primitive types in order to determine if they are equivalent.
 * @param {Array<T>} a The array to compare.
 * @param {Array<U>} b The other array to compare.
 * @returns {boolean} Returns `true` if both arrays are equivalent.
 */
export const compareFieldArray = (a, b) => {
  const prev = Array.isArray(a) ? a : [];
  const curr = Array.isArray(b) ? b : [];

  if (prev.length !== curr.length) {
    return false;
  }

  return _isEqual(_sortBy(prev), _sortBy(curr));
};

/**
 * Compares two arrays of objects in order to determine if they are equivalent.
 * @param {Array<T>} a The array of objects to compare.
 * @param {Array<U>} b The other array of objects to compare.
 * @param {{sort: (iteratee: T | U) => any[]: comparator: (a: T, b: U) => T[]}} options The options to control sorting and comparisons.
 * @returns {boolean} Returns `true` if both arrays are equivalent.
 */
export const compareFieldObjectArray = (a, b, { sort, comparator }) => {
  const prev = Array.isArray(a) ? a : [];
  const curr = Array.isArray(b) ? b : [];

  if (prev.length !== curr.length) {
    return false;
  }

  const common = _intersectionWith(_sortBy(prev, sort), _sortBy(curr, sort), comparator);
  return prev.length === common.length && curr.length === common.length;
};
